/**
 * Reiter "Fußball": vier Spalten (Masse, Caller, Backstep, Helitrupp), in die
 * Mitglieder aus der Mitgliederverwaltung eingetragen werden - je Eintrag mit
 * freiem Notizfeld. Die Auswahlliste lädt beim Öffnen der Seite sowie bei
 * jedem Fokus/Öffnen des Dropdowns neu (GET /api/football/members-list), ein
 * frisch angelegtes Mitglied erscheint dort also ohne Neuladen der Seite.
 * Zugriff (Migration 0069): "football.view" = ansehen/lesen (alle Rollen außer
 * Routenpartner), "football.manage" = eintragen, entfernen und Notizen
 * bearbeiten (ab Werk nur President). Ohne "football.manage" ist die Seite
 * rein lesend: kein Dropdown, kein "Entfernen", Notizen nur als Text.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import type { FootballAssignment, FootballPosition } from "../../types";

const COLUMNS: { key: FootballPosition; label: string }[] = [
  { key: "masse", label: "Masse" },
  { key: "caller", label: "Caller" },
  { key: "backstep", label: "Backstep" },
  { key: "helitrupp", label: "Helitrupp" },
];

interface MemberOption { id: number; first_name: string; last_name: string; rank: string | null }

const displayName = (m: { first_name: string; last_name: string }) => `${m.first_name} ${m.last_name}`.trim();

export default function FootballPage() {
  const canManage = usePermission("football.manage");
  const [assignments, setAssignments] = useState<FootballAssignment[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    try {
      setMembers(await api.get<MemberOption[]>("/api/football/members-list"));
    } catch {
      // Auswahlliste bleibt auf dem letzten Stand - kein Abbruch der Seite.
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    setAssignments(await api.get<FootballAssignment[]>("/api/football"));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // Die Mitgliederauswahl wird nur zum Bearbeiten gebraucht (und ist serverseitig nur dafür freigegeben).
        await Promise.all([loadAssignments(), canManage ? loadMembers() : Promise.resolve()]);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Laden fehlgeschlagen.");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadAssignments, loadMembers, canManage]);

  async function handleAdd(position: FootballPosition, memberId: number) {
    setError(null);
    try {
      await api.post("/api/football", { position, memberId });
      await loadAssignments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Eintragen fehlgeschlagen.");
    }
  }

  async function handleRemove(a: FootballAssignment, label: string) {
    if (!confirm(`"${displayName(a)}" aus "${label}" entfernen? Die Notiz geht dabei verloren.`)) return;
    setError(null);
    try {
      await api.delete(`/api/football/${a.id}`);
      await loadAssignments();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Entfernen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Fußball</h1>
        <p className="text-sm text-gray-500 mt-1">
          {canManage
            ? "Mitglieder den Positionen zuordnen - die Auswahl ist immer mit der Mitgliederliste synchron."
            : "Übersicht der Positionen (nur ansehen)."}
        </p>
      </div>

      {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

      {loading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
          {COLUMNS.map((col) => {
            const entries = assignments.filter((a) => a.position === col.key);
            const taken = new Set(entries.map((a) => a.member_id));
            const available = members.filter((m) => !taken.has(m.id));
            return (
              <Card key={col.key} padded={false} className="overflow-hidden">
                <div className="px-4 py-3 border-b border-surface-border">
                  <h2 className="font-display text-base font-semibold text-gray-100 uppercase tracking-wide">{col.label}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{entries.length} {entries.length === 1 ? "Mitglied" : "Mitglieder"}</p>
                </div>

                <div className="p-3 space-y-3">
                  {entries.length === 0 && <p className="text-sm text-gray-500 px-1">Noch niemand eingetragen.</p>}
                  {entries.map((a) => (
                    <MemberEntry key={a.id} entry={a} canManage={canManage} onRemove={() => handleRemove(a, col.label)} />
                  ))}

                  {canManage && (
                  <select
                    className="input text-sm"
                    value=""
                    onFocus={loadMembers}
                    onMouseDown={loadMembers}
                    onChange={(e) => { if (e.target.value) handleAdd(col.key, Number(e.target.value)); }}
                    aria-label={`Mitglied zu ${col.label} hinzufügen`}
                  >
                    <option value="">+ Mitglied hinzufügen…</option>
                    {available.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.last_name ? `${m.last_name}, ${m.first_name}` : m.first_name}{m.rank ? ` — ${m.rank}` : ""}
                      </option>
                    ))}
                  </select>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Ein eingetragenes Mitglied mit leerem Freitext-Notizfeld - gespeichert wird
// beim Verlassen des Felds bzw. mit Enter, aber nur bei geänderter Notiz.
function MemberEntry({ entry, canManage, onRemove }: { entry: FootballAssignment; canManage: boolean; onRemove: () => void }) {
  const [value, setValue] = useState(entry.note ?? "");
  const [saved, setSaved] = useState(entry.note ?? "");
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  async function save() {
    if (value.trim() === saved.trim()) return;
    setState("saving");
    try {
      await api.put(`/api/football/${entry.id}/note`, { note: value });
      setSaved(value);
      setState("saved");
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (err) {
      setState("error");
      alert(err instanceof ApiError ? err.message : "Notiz konnte nicht gespeichert werden.");
    }
  }

  return (
    <div className="bg-surface-raised rounded-lg px-3 py-2.5 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-100 font-medium break-words">{displayName(entry)}</p>
          {entry.rank && <p className="text-[11px] text-gray-500">{entry.rank}</p>}
        </div>
        {canManage && <button className="text-xs text-danger hover:underline shrink-0" onClick={onRemove}>Entfernen</button>}
      </div>
      {!canManage ? (
        entry.note ? <p className="text-sm text-gray-300 break-words whitespace-pre-wrap">{entry.note}</p> : null
      ) : (
      <div className="flex items-center gap-2">
        <input
          className="input py-1 text-sm"
          value={value}
          maxLength={500}
          placeholder="Notiz…"
          onChange={(e) => { setValue(e.target.value); if (state !== "saving") setState("idle"); }}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        />
        <span className="text-[11px] w-10 shrink-0 text-gray-500">
          {state === "saving" ? "…" : state === "saved" ? <span className="text-success">✓</span> : state === "error" ? <span className="text-danger">Fehler</span> : ""}
        </span>
      </div>
      )}
    </div>
  );
}
